import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ROLE_CATALOG, ROLE_LABELS } from '../roles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoleLegend = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="role-legend">
    {ROLE_CATALOG.map((r) => (
      <div key={r.value} className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`role-card-${r.value}`}>
        <h4 className="font-bold text-gray-900">{r.label}</h4>
        <p className="text-sm text-gray-600 mt-1 mb-3">{r.summary}</p>
        <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Puede</p>
        <ul className="text-xs text-gray-700 mt-1 space-y-1">
          {r.can.map((c, i) => <li key={i}>• {c}</li>)}
        </ul>
        <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mt-3">No puede</p>
        <ul className="text-xs text-gray-500 mt-1 space-y-1">
          {r.cannot.map((c, i) => <li key={i}>• {c}</li>)}
        </ul>
      </div>
    ))}
  </div>
);

const UsersManagement = () => {
  const [data, setData] = useState({ users: [], active_admins: 0, company_name: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', password: '', role: 'auditor' });
  const [saving, setSaving] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/company/users`, { headers: auth() });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(null), 6000); };

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/company/users`, form, { headers: auth() });
      flash(`Usuario ${form.email} creado como ${ROLE_LABELS[form.role]}. Pasale la contraseña que definiste; puede cambiarla desde "Olvidé mi contraseña".`);
      setForm({ email: '', full_name: '', password: '', role: 'auditor' });
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'No se pudo crear el usuario');
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await axios.put(`${API}/company/users/${u.id}/role`, { role }, { headers: auth() });
      flash(`${u.email} ahora es ${ROLE_LABELS[role]}.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cambiar el rol');
    }
  };

  const toggleStatus = async (u) => {
    try {
      await axios.put(`${API}/company/users/${u.id}/status`, { is_active: !u.is_active }, { headers: auth() });
      flash(`${u.email} ${u.is_active ? 'desactivado' : 'reactivado'}.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cambiar el estado');
    }
  };

  const resetPassword = async (u) => {
    const pwd = window.prompt(`Nueva contraseña para ${u.email} (mínimo 8 caracteres):`);
    if (!pwd) return;
    try {
      await axios.put(`${API}/company/users/${u.id}/password`, { password: pwd }, { headers: auth() });
      flash(`Contraseña de ${u.email} actualizada. Pasásela por un canal seguro.`);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cambiar la contraseña');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6" data-testid="users-management">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <span className="mr-3">👥</span> Usuarios de {data.company_name || 'tu empresa'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Cada usuario que trabaje con Costillatech.com necesita su propio acceso. El rol define qué puede hacer:
            elegilo con criterio, porque habilita acciones con consecuencias legales (autorizaciones de escaneo)
            y económicas (plan y facturación).
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
          data-testid="new-user-btn"
        >
          {showForm ? 'Cancelar' : '+ Nuevo usuario'}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 my-4 text-sm text-blue-900" data-testid="users-help">
        El registro público de Costillatech.com crea <strong>empresas nuevas</strong> y a su primer usuario como
        Administrador. Para sumar gente a <strong>esta</strong> empresa, creala desde acá: así el acceso queda
        auditado y con el rol correcto.
      </div>

      {notice && <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg p-3 mb-4 text-sm" data-testid="users-notice">{notice}</div>}
      {error && <div className="bg-red-50 border border-red-300 text-red-800 rounded-lg p-3 mb-4 text-sm" data-testid="users-error">{error}</div>}

      {showForm && (
        <form onSubmit={createUser} className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50" data-testid="new-user-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
              <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="new-user-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="new-user-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña inicial (mín. 8)</label>
              <input required minLength={8} type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono" data-testid="new-user-password" />
              <p className="text-xs text-gray-500 mt-1">Se la pasás vos por un canal seguro. El usuario puede cambiarla luego.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md" data-testid="new-user-role">
                {ROLE_CATALOG.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <p className="text-xs text-gray-600 mt-1" data-testid="new-user-role-help">
                {ROLE_CATALOG.find((r) => r.value === form.role)?.summary}
              </p>
            </div>
          </div>
          <button type="submit" disabled={saving}
            className="mt-4 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold"
            data-testid="create-user-submit">
            {saving ? 'Creando...' : 'Crear usuario'}
          </button>
        </form>
      )}

      <RoleLegend />

      {loading ? (
        <p className="text-gray-500" data-testid="users-loading">Cargando usuarios...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-left">Rol</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Último acceso</th>
                <th className="px-4 py-3 text-left">Origen</th>
                <th className="px-4 py-3 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{u.full_name || '—'}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === 'superadmin' ? (
                      <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-bold">Super Administrador</span>
                    ) : (
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded" data-testid={`user-role-select-${u.email}`}>
                        {ROLE_CATALOG.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {u.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {u.last_login ? new Date(u.last_login).toLocaleString('es-AR') : 'Nunca ingresó'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {u.created_via === 'admin_panel' ? 'Creado por un admin' : 'Registro público'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {u.role !== 'superadmin' && (
                        <>
                          <button onClick={() => toggleStatus(u)}
                            className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                            data-testid={`toggle-status-${u.email}`}>
                            {u.is_active ? 'Desactivar' : 'Reactivar'}
                          </button>
                          <button onClick={() => resetPassword(u)}
                            className="px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs"
                            data-testid={`reset-password-${u.email}`}>
                            Cambiar contraseña
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-3" data-testid="admins-count">
            Administradores activos: {data.active_admins}. Costillatech.com no permite dejar la empresa sin ningún
            administrador activo, ni que te cambies el rol o desactives tu propio usuario.
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
